/**
 * Pick durable watchlist identities from a live signal (and siblings that
 * share its advertised name). DFP matches each row independently, so enrolling
 * Classic MAC + hotspot SSID means a hit on either one is enough.
 */

export type WatchlistIdType = 'mac' | 'bssid' | 'ssid' | 'name' | 'device_id' | 'other';

export type WatchlistSignalType = 'any' | 'wifi' | 'bluetooth' | 'cellular';

export interface WatchlistIdentity {
  identifier: string;
  identifier_type: WatchlistIdType;
  signal_type: WatchlistSignalType;
  selected: boolean;
  note: string;
}

export interface WatchlistBundle {
  identities: WatchlistIdentity[];
  severity_floor: 'info' | 'warning' | 'critical';
  label: string;
  reason: string;
}

export interface SignalIdentitySource {
  signal_type?: string;
  address?: string | null;
  name?: string | null;
  bssid?: string | null;
  ssid?: string | null;
  bt_type?: string | null;
  is_randomized?: boolean | null;
  cell_tower_id?: string | null;
}

export const compactMac = (value: string): string =>
  value.toLowerCase().replace(/[^0-9a-f]/g, '');

export const looksLikeMac = (value: string): boolean => compactMac(value).length === 12;

/** IEEE U/L bit — set means the address is locally administered / randomized. */
export const isLocallyAdministered = (mac: string): boolean => {
  const hex = compactMac(mac);
  if (hex.length < 2) return false;
  return (parseInt(hex.slice(0, 2), 16) & 0x02) !== 0;
};

const normalizeName = (value?: string | null): string =>
  (value || '').trim().replace(/^"|"$/g, '').toLowerCase();

const isBleNoiseName = (name: string): boolean =>
  /^ble#/i.test(name) || /^unnamed/i.test(name);

const isClassic = (btType?: string | null): boolean => {
  const t = (btType || '').toUpperCase();
  return t === 'CLASSIC' || t === 'BR/EDR' || t === 'BR_EDR';
};

const identityKey = (row: WatchlistIdentity): string => {
  const id = row.identifier_type === 'mac' || row.identifier_type === 'bssid'
    ? compactMac(row.identifier)
    : row.identifier.trim().toLowerCase();
  return `${row.signal_type}:${row.identifier_type}:${id}`;
};

const pushUnique = (out: WatchlistIdentity[], row: WatchlistIdentity) => {
  if (!row.identifier.trim()) return;
  const key = identityKey(row);
  if (out.some((existing) => identityKey(existing) === key)) return;
  out.push(row);
};

const displayName = (s: SignalIdentitySource): string => {
  if (s.signal_type === 'wifi') return (s.ssid || '').trim().replace(/^"|"$/g, '');
  if (s.signal_type === 'bluetooth') return (s.name || '').trim();
  return '';
};

/** Same advertised name on another radio — DoubleCrossX BT ↔ DoubleCrossX hotspot. */
export const sharesAdvertisedName = (a: SignalIdentitySource, b: SignalIdentitySource): boolean => {
  const left = normalizeName(displayName(a));
  if (!left || isBleNoiseName(left)) return false;
  return normalizeName(displayName(b)) === left;
};

const collectFromSignal = (s: SignalIdentitySource, out: WatchlistIdentity[]) => {
  if (s.signal_type === 'bluetooth') {
    const addr = (s.address || '').trim();
    const name = (s.name || '').trim();
    const randomized = Boolean(s.is_randomized) || (!isClassic(s.bt_type) && !!addr && isLocallyAdministered(addr));

    if (addr && looksLikeMac(addr)) {
      if (randomized) {
        pushUnique(out, {
          identifier: addr,
          identifier_type: 'mac',
          signal_type: 'bluetooth',
          selected: false,
          note: 'Randomized BLE address — usually rotates',
        });
      } else {
        pushUnique(out, {
          identifier: addr,
          identifier_type: 'mac',
          signal_type: 'bluetooth',
          selected: true,
          note: isClassic(s.bt_type) || !isLocallyAdministered(addr)
            ? 'Bluetooth hardware address (stable)'
            : 'Bluetooth address',
        });
      }
    }
    if (name && !isBleNoiseName(name)) {
      pushUnique(out, {
        identifier: name,
        identifier_type: 'name',
        signal_type: 'bluetooth',
        selected: true,
        note: 'Bluetooth advertised name',
      });
    }
    return;
  }

  if (s.signal_type === 'wifi') {
    const ssid = (s.ssid || '').trim().replace(/^"|"$/g, '');
    const bssid = (s.bssid || '').trim();
    if (ssid) {
      pushUnique(out, {
        identifier: ssid,
        identifier_type: 'ssid',
        signal_type: 'wifi',
        selected: true,
        note: 'Wi-Fi SSID — survives hotspot MAC rotation',
      });
    }
    if (bssid && looksLikeMac(bssid)) {
      const rotating = isLocallyAdministered(bssid);
      pushUnique(out, {
        identifier: bssid,
        identifier_type: 'bssid',
        signal_type: 'wifi',
        selected: !rotating,
        note: rotating
          ? 'Hotspot / randomized BSSID — often changes when toggled'
          : 'Wi-Fi BSSID (globally administered)',
      });
    }
    return;
  }

  if (s.signal_type === 'cellular' && s.cell_tower_id) {
    pushUnique(out, {
      identifier: String(s.cell_tower_id).trim(),
      identifier_type: 'other',
      signal_type: 'cellular',
      selected: true,
      note: 'Cell identifier',
    });
  }
};

export const recommendWatchlistIdentities = (
  primary: SignalIdentitySource,
  siblings: SignalIdentitySource[] = [],
): WatchlistIdentity[] => {
  const out: WatchlistIdentity[] = [];
  collectFromSignal(primary, out);
  for (const sibling of siblings) {
    if (sibling === primary) continue;
    if (!sharesAdvertisedName(primary, sibling)) continue;
    collectFromSignal(sibling, out);
  }
  return out;
};

export const bundleWatchlistDraft = (
  primary: SignalIdentitySource,
  siblings: SignalIdentitySource[] = [],
): WatchlistBundle | null => {
  const identities = recommendWatchlistIdentities(primary, siblings);
  if (!identities.length) return null;
  const label = (
    displayName(primary)
    || identities.find((row) => row.selected)?.identifier
    || identities[0].identifier
  ).slice(0, 120);
  return {
    identities,
    severity_floor: 'critical',
    label,
    reason: '',
  };
};

export const selectedWatchlistIdentities = (bundle: WatchlistBundle): WatchlistIdentity[] =>
  bundle.identities.filter((row) => row.selected && row.identifier.trim());
